import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

const DataCacheContext = createContext();

// Matches the backend's CACHE_TTL_DEFAULT (10 min) so the frontend cache doesn't
// outlive the data it's mirroring for the life of the browser tab.
const CACHE_TTL_MS = 10 * 60 * 1000;

export const useDataCache = () => {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error("useDataCache must be used within a DataCacheProvider");
  }
  return context;
};

export const DataCacheProvider = ({ children, currentClientId }) => {
  const [cache, setCache] = useState({});
  // Use a ref to hold the latest cache so getCachedData has a stable reference
  const cacheRef = useRef(cache);
  cacheRef.current = cache;

  useEffect(() => {
    if (currentClientId) {
      const cacheKey = `client_${currentClientId}`;
      setCache((prev) => {
        if (!prev[cacheKey]) {
          return {
            ...prev,
            [cacheKey]: {},
          };
        }
        return prev;
      });
    }
  }, [currentClientId]);

  // Stable reference - reads from ref instead of depending on cache state
  const getCachedData = useCallback(
    (key, clientId) => {
      if (!clientId) return undefined;
      const cacheKey = `client_${clientId}`;
      const entry = cacheRef.current[cacheKey]?.[key];
      if (entry === undefined) return undefined;
      if (Date.now() - entry.cachedAt > CACHE_TTL_MS) return undefined;
      return entry.data;
    },
    [] // No dependency on cache - uses ref instead
  );

  const setCachedData = useCallback(
    (key, data, clientId) => {
      if (!clientId) return;
      const cacheKey = `client_${clientId}`;
      const updated = {
        ...cacheRef.current,
        [cacheKey]: {
          ...(cacheRef.current[cacheKey] || {}),
          [key]: { data, cachedAt: Date.now() },
        },
      };
      cacheRef.current = updated;
      setCache(updated);
    },
    []
  );

  const clearCache = useCallback((clientId) => {
    if (clientId) {
      const cacheKey = `client_${clientId}`;
      setCache((prev) => {
        const newCache = { ...prev };
        delete newCache[cacheKey];
        return newCache;
      });
    } else {
      setCache({});
    }
  }, []);

  const value = {
    getCachedData,
    setCachedData,
    clearCache,
  };

  return <DataCacheContext.Provider value={value}>{children}</DataCacheContext.Provider>;
};

